<template>
  <div class="doc-uploader">
    <div class="doc-uploader__input">
      <input
        :ref="inputId"
        :disabled="disabled || isLoading"
        type="file"
        @change="uploadDocument"
      />
    </div>

    <slot name="default">
      <p class="font-size--15 mb-2 text-center">{{ label }}</p>
    </slot>

    <slot name="activator" :uploader="openInput" :loading="isLoading" :disabled="isDisabled">
      <div class="doc-uploader__activator">
        <p class="attach" :class="{ complete: isDisabled }" @click="openInput()">
          <template v-if="isLoading">
            <v-progress-circular indeterminate size="20" />
          </template>
          <template v-else>
            {{ attachText }}
          </template>
        </p>
      </div>
    </slot>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

export default {
  props: {
    documentKey: {
      type: String,
      required: true,
      default: null,
    },
    label: {
      type: String,
      default: null,
    },
    originFilePath: {
      type: String,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      mimeTypes: [
        'image/gif',
        'image/png',
        'image/jpeg',
        'image/bmp',
        'image/webp',
        'application/pdf',
      ],
      uploadLoading: false,
      origin: null,
      file: null,
    }
  },
  computed: {
    ...mapGetters('document', {
      isAddLoading: 'getAddLoading',
    }),
    inputId() {
      return `ref-input_${this.documentKey}`
    },
    hasFile() {
      return !!this.origin
    },
    isLoading() {
      return this.uploadLoading || this.isAddLoading
    },
    isDisabled() {
      return this.disabled || this.isLoading
    },
    attachText() {
      let txt = '파일 올리기'
      if (this.disabled) {
        txt = '첨부 완료'
      } else if (this.hasFile) {
        txt = '수정하기'
      }

      return txt
    },
  },
  watch: {
    originFilePath: {
      immediate: true,
      handler(v) {
        this.origin = v
      },
    },
    file(v) {
      if (v) {
        this.$emit('update:document', v)
      }
    },
  },
  methods: {
    ...mapActions('document', {
      addStoreDocuments: 'addDocuments',
    }),
    async openInput() {
      if (this.isDisabled) {
        return
      }

      if (this.hasFile) {
        const { isConfirmed } = await this.$swal.basic.confirm('이미 업로드된 파일을 수정합니다')
        if (!isConfirmed) {
          return
        }
      }

      this.$refs[this.inputId].click()
    },
    async uploadDocument(e) {
      if (this.isDisabled) {
        return
      }

      this.uploadLoading = true
      const file = e.target.files[0]
      if (!file) {
        this.uploadLoading = false
        return
      }

      if (this.mimeTypes.indexOf(file.type) < 0) {
        await this.$swal.basic.error('서류는 jpg, png, pdf 파일만 업로드하실 수 있습니다.')
        this.uploadLoading = false
        return
      }

      try {
        const result = await this.addStoreDocuments({
          key: this.documentKey,
          file,
        })
        const originKey = result?.[this.documentKey] || null
        if (originKey) {
          this.origin = originKey
          this.file = file
          this.$swal.basic.success(`${this.label}이(가) 업로드되었습니다.`)
        } else {
          throw new Error('Does not exist file key')
        }
      } catch (e) {
        this.$swal.basic.error(
          `${this.label} 서류 업로드중 에러가 발생하였습니다. 다시 시도해주세요.`,
        )
      } finally {
        this.uploadLoading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/common/_variables';

.doc-uploader {
  &__input {
    display: none;
    width: 0;
    height: 0;
  }

  &__activator {
    p {
      margin: 0;
    }

    .attach {
      min-width: 105px;
      background-color: $main-color-default;
      border-radius: $border-radius-default;
      color: $white;
      cursor: pointer;
      font-size: 15px;
      text-align: center;

      padding: 8px 16px;

      &.complete {
        background-color: $gray-fill;
      }
    }
  }
}
</style>
